(() => {
  const TDShipDate = {
    CACHE_PREFIX: 'td-ship-date:v2:',
    CACHE_TTL_MS: 60 * 60 * 1000,
    // Pages without a date (e.g. in stock) are re-checked sooner than real dates expire.
    MISS_CACHE_TTL_MS: 10 * 60 * 1000,
    FETCH_TIMEOUT_MS: 8000,
    RANGE_DAYS: 3,
    SHIP_DATE_PATTERN: /Expected Ship Date of\s+([A-Za-z]+)\.?\s+(\d{1,2})/i,
    MONTHS: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
    MONTH_LABELS: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],

    async formatRange(url) {
      if (!url || url.length <= 5) return null;

      const shipDate = await this.getShipDate(url);
      return shipDate ? this.formatDateRange(shipDate) : null;
    },

    // One lookup per URL per page view: re-renders and duplicate options share the same promise.
    pending: new Map(),

    getShipDate(url) {
      if (!this.pending.has(url)) this.pending.set(url, this.lookupShipDate(url));
      return this.pending.get(url);
    },

    async lookupShipDate(url) {
      const cached = this.readCache(url);
      if (cached !== undefined) return cached;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), this.FETCH_TIMEOUT_MS);

      try {
        const response = await fetch(url, {
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        });
        if (!response.ok) return null;

        const shipDate = this.parseShipDate(await response.text(), url);
        this.writeCache(url, shipDate);
        return shipDate;
      } catch {
        return null;
      } finally {
        clearTimeout(timeout);
      }
    },

    parseShipDate(html, url) {
      const text = this.shipDateText(html, url);
      const match = text && text.match(this.SHIP_DATE_PATTERN);
      if (!match) return null;

      const monthIndex = this.MONTHS.indexOf(match[1].slice(0, 3).toLowerCase());
      const day = parseInt(match[2], 10);
      if (monthIndex === -1 || day < 1 || day > 31) return null;

      return this.closestDate(monthIndex, day);
    },

    // The source page lists every variant's date in a hidden JSON map, so the
    // date must be looked up by the source variant id rather than first match.
    shipDateText(html, url) {
      let sourceVariantId = null;
      try {
        sourceVariantId = new URL(url, window.location.href).searchParams.get('variant');
      } catch {
        return null;
      }

      if (!sourceVariantId) {
        return html.replace(/<script\b[\s\S]*?<\/script>/gi, '');
      }

      const inventory = this.extractJsonObject(html, 'variants_inventory');
      if (Number(inventory?.[sourceVariantId]) > 0) return null;

      const backorders = this.extractJsonObject(html, 'backorder_length');
      const entry = backorders?.[sourceVariantId];
      return typeof entry === 'string' ? entry : null;
    },

    extractJsonObject(html, key) {
      const match = html.match(new RegExp(`"${key}"\\s*:\\s*(\\{[\\s\\S]*?\\})`));
      if (!match) return null;

      try {
        return JSON.parse(match[1]);
      } catch {
        return null;
      }
    },

    closestDate(monthIndex, day) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const year = today.getFullYear();

      return [year - 1, year, year + 1]
        .map((y) => new Date(y, monthIndex, day))
        .filter((date) => date.getMonth() === monthIndex)
        .reduce(
          (closest, date) =>
            !closest || Math.abs(date - today) < Math.abs(closest - today) ? date : closest,
          null
        );
    },

    formatDateRange(date) {
      const days = this.RANGE_DAYS;
      const start = new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);
      const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
      return `${this.formatDay(start)} - ${this.formatDay(end)}`;
    },

    formatDay(date) {
      const day = date.getDate();
      return `${this.MONTH_LABELS[date.getMonth()]} ${day}${this.ordinal(day)}`;
    },

    ordinal(day) {
      if (day % 100 >= 11 && day % 100 <= 13) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    },

    // Returns a Date, null for a cached "no date", or undefined when nothing usable is cached.
    readCache(url) {
      try {
        const raw = sessionStorage.getItem(this.CACHE_PREFIX + url);
        if (!raw) return undefined;

        const { iso, ts } = JSON.parse(raw);
        const ttl = iso ? this.CACHE_TTL_MS : this.MISS_CACHE_TTL_MS;
        if (Date.now() - ts > ttl) return undefined;
        if (!iso) return null;

        const [y, m, d] = iso.split('-').map(Number);
        return new Date(y, m - 1, d);
      } catch {
        return undefined;
      }
    },

    writeCache(url, date) {
      const iso = date
        ? [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0'),
          ].join('-')
        : null;

      try {
        sessionStorage.setItem(this.CACHE_PREFIX + url, JSON.stringify({ iso, ts: Date.now() }));
      } catch {}
    },
  };

  window.TDShipDate = TDShipDate;

  if (customElements.get('td-ship-date-range')) return;

  customElements.define(
    'td-ship-date-range',
    class TdShipDateRange extends HTMLElement {
      constructor() {
        super();
        this.urls = this.parseUrls();
        this.output = this.querySelector('[data-ship-date-output]');
        this.requestToken = 0;
        this.unsubscribeVariantChange = undefined;
      }

      connectedCallback() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!this.isOwnSection(data?.sectionId)) return;
            this.update(data?.variant?.id);
          });
        }

        this.update(this.dataset.selectedVariantId);
      }

      isOwnSection(sectionId) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !sectionId) return true;
        return sectionId === (productInfo.dataset.originalSection || productInfo.dataset.section);
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      parseUrls() {
        const script = this.querySelector('[data-ship-date-urls]');
        if (!script) return {};

        try {
          return JSON.parse(script.textContent) || {};
        } catch {
          return {};
        }
      }

      async update(variantId) {
        const token = ++this.requestToken;
        const url = variantId ? this.urls[String(variantId)] : null;
        const range = await TDShipDate.formatRange(url);
        if (token !== this.requestToken) return;

        if (!range) {
          this.hide();
          return;
        }

        this.show(range);
      }

      show(text) {
        this.hidden = false;
        if (this.output) this.output.textContent = text;
      }

      hide() {
        if (this.output) this.output.textContent = '';
        this.hidden = true;
      }
    }
  );
})();
